import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { GoogleMap, PolylineF, useJsApiLoader } from "@react-google-maps/api";
import {
  getStoredData,
  saveToGlobalState,
  removeSpecificData,
} from "../../utils/localStorage";
import StepFooter from "../../components/step-footer/StepFooter";
import { LocationOn } from "@mui/icons-material";
import "./CadastroViagem.css";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

const containerStyle = {
  width: "100%",
  height: "100%",
  //minHeight: "320px",
};
const defaultCenter = { lat: -15.7801, lng: -47.9292 };
const defaultZoom = 4.8;
const airportZoom = 14;
const mapId = "DEMO_MAP_ID";

function getTextValue(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value.toString === "function") return value.toString();
  return "";
}

function getPlaceName(place) {
  return (
    place?.name || place?.displayName || getTextValue(place?.displayName) || ""
  );
}

function getFormattedAddress(place) {
  return place?.formattedAddress || place?.formatted_address || "";
}

function getLocationLiteral(location) {
  if (!location) return null;

  const lat =
    typeof location.lat === "function" ? location.lat() : location.lat;
  const lng =
    typeof location.lng === "function" ? location.lng() : location.lng;

  if (typeof lat !== "number" || typeof lng !== "number") return null;
  return { lat, lng };
}

function normalizeAddressComponents(place) {
  const raw = place?.addressComponents || place?.address_components || [];

  return raw.map((component) => ({
    long_name: component.longText ?? component.long_name ?? "",
    short_name: component.shortText ?? component.short_name ?? "",
    types: component.types || [],
  }));
}

function translateAirport(text = "") {
  return text
    .replace(/International Airport/gi, "Aeroporto Internacional")
    .replace(/Domestic Airport/gi, "Aeroporto Doméstico")
    .replace(/Airport/gi, "Aeroporto")
    .replace(/ - /g, " - ");
}

function isAirport(place) {
  const name = (getPlaceName(place) || "").toLowerCase();
  const types = place?.types || [];

  const isAirportType =
    types.includes("airport") || types.includes("international_airport");

  const blocked = [
    "forca aerea",
    "força aérea",
    "air force",
    "military",
    "base",
    "aeroclube",
    "heliporto",
    "heliponto",
    "executivo",
    "private",
    "aeronautica",
    "aeronáutica",
    "radar",
    "weather",
    "small_airport",
    "augusto severo",
  ];

  const isBlocked = blocked.some((w) => name.includes(normalizeText(w)));

  const looksDeprecated =
    name.includes("augusto severo") ||
    name.includes("old airport") ||
    name.includes("antigo aeroporto");

  const status = String(place?.businessStatus || "").toUpperCase();
  const isOperational = status === "OPERATIONAL" || status === "";

  return isAirportType && !isBlocked && !looksDeprecated && isOperational;
}

function isBrazilPlace(place) {
  const address = (
    place?.formatted_address ||
    place?.formattedAddress ||
    ""
  ).toLowerCase();

  const components = place?.address_components || [];

  const isBR = components.some(
    (c) => c.types?.includes("country") && c.short_name === "BR",
  );

  return address.includes("brasil") || isBR;
}

function buildStoredAirport(stored, prefix) {
  const name = stored[`cidade_${prefix}`] || "";
  const mainCity = stored[`cidade_${prefix}_main`] || "";
  const searchValue = stored[`search_${prefix}`] || "";

  const lat = Number(stored[`lat_cidade_${prefix}`]);
  const lng = Number(stored[`lng_cidade_${prefix}`]);

  const placeId =
    stored[`place_id_${prefix}`] || stored[`airport_${prefix}_place_id`] || "";

  if (!name || Number.isNaN(lat) || Number.isNaN(lng)) return null;

  return {
    place_id: placeId,
    name,
    mainCity,
    searchValue,
    position: { lat, lng },
  };
}

function matchesSearch(place, query) {
  const text =
    `${getPlaceName(place)} ${getFormattedAddress(place)}`.toLowerCase();
  const q = query.toLowerCase();

  return text.includes(q);
}

function createMarkerContent(label) {
  const el = document.createElement("div");
  el.textContent = label;
  el.style.width = "28px";
  el.style.height = "28px";
  el.style.borderRadius = "999px";
  el.style.display = "flex";
  el.style.alignItems = "center";
  el.style.justifyContent = "center";
  el.style.background = "#1e88e5";
  el.style.color = "#fff";
  el.style.fontSize = "14px";
  el.style.fontWeight = "700";
  el.style.boxShadow = "0 2px 8px rgba(0,0,0,0.25)";
  el.style.border = "2px solid #fff";
  return el;
}

function normalizeText(text = "") {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

const AIRPORT_CITY_MAP = [
  // SP
  { match: ["gru", "guarulhos"], city: "São Paulo" },
  { match: ["cgh", "congonhas"], city: "São Paulo" },
  { match: ["vcp", "viracopos", "campinas"], city: "Campinas" },
  { match: ["sao jose dos campos"], city: "São José dos Campos" },
  { match: ["ribeirao preto"], city: "Ribeirão Preto" },

  // RJ
  { match: ["gig", "galeao"], city: "Rio de Janeiro" },
  { match: ["sdu", "santos dumont"], city: "Rio de Janeiro" },
  { match: ["macae"], city: "Macaé" },
  { match: ["cabo frio"], city: "Cabo Frio" },

  // MG
  { match: ["cnf", "confins"], city: "Belo Horizonte" },
  { match: ["plu", "pampulha"], city: "Belo Horizonte" },
  { match: ["uberlandia"], city: "Uberlândia" },
  { match: ["uberaba"], city: "Uberaba" },

  // ES
  { match: ["vitoria"], city: "Vitória" },

  // SUL
  { match: ["poa", "porto alegre"], city: "Porto Alegre" },
  { match: ["cwb", "curitiba"], city: "Curitiba" },
  { match: ["fln", "florianopolis"], city: "Florianópolis" },
  { match: ["navegantes"], city: "Navegantes" },
  { match: ["joinville"], city: "Joinville" },
  { match: ["londrina"], city: "Londrina" },
  { match: ["maringa"], city: "Maringá" },
  { match: ["foz do iguacu"], city: "Foz do Iguaçu" },

  // CENTRO-OESTE
  { match: ["bsb", "brasilia"], city: "Brasília" },
  { match: ["gyn", "goiania"], city: "Goiânia" },
  { match: ["cuiaba"], city: "Cuiabá" },
  { match: ["campo grande"], city: "Campo Grande" },

  // NORDESTE
  { match: ["nat", "natal"], city: "Natal" },
  { match: ["rec", "recife"], city: "Recife" },
  { match: ["ssa", "salvador"], city: "Salvador" },
  { match: ["for", "fortaleza"], city: "Fortaleza" },
  { match: ["mcz", "maceio"], city: "Maceió" },
  { match: ["aju", "aracaju"], city: "Aracaju" },
  { match: ["jpa", "joao pessoa"], city: "João Pessoa" },
  { match: ["slz", "sao luis"], city: "São Luís" },
  { match: ["teresina"], city: "Teresina" },
  { match: ["porto seguro"], city: "Porto Seguro" },
  { match: ["ilheus"], city: "Ilhéus" },

  // NORTE
  { match: ["mao", "manaus"], city: "Manaus" },
  { match: ["bel", "belem"], city: "Belém" },
  { match: ["pvh", "porto velho"], city: "Porto Velho" },
  { match: ["rio branco"], city: "Rio Branco" },
  { match: ["boa vista"], city: "Boa Vista" },
  { match: ["macapa"], city: "Macapá" },
  { match: ["palmas"], city: "Palmas" },
  { match: ["santarem"], city: "Santarém" },

  // OUTROS
  { match: ["chapeco"], city: "Chapecó" },
  { match: ["caxias do sul"], city: "Caxias do Sul" },
  { match: ["pelotas"], city: "Pelotas" },
  { match: ["montes claros"], city: "Montes Claros" },
  { match: ["juiz de fora"], city: "Juiz de Fora" },
];

function getCityFromMap(place) {
  const name = normalizeText(place?.name || place?.displayName || "");
  const address = normalizeText(
    place?.formatted_address || place?.formattedAddress || "",
  );

  const text = ` ${name} ${address} `;

  for (const item of AIRPORT_CITY_MAP) {
    if (
      item.match.some((term) => {
        const t = ` ${normalizeText(term)} `;
        return text.includes(t);
      })
    ) {
      return item.city;
    }
  }

  return null;
}

function getCityFromAddress(place) {
  const components = place.address_components || [];

  const city =
    components.find((c) => c.types.includes("locality"))?.long_name ||
    components.find((c) => c.types.includes("administrative_area_level_2"))
      ?.long_name ||
    "";

  const state =
    components.find((c) => c.types.includes("administrative_area_level_1"))
      ?.short_name || "";

  const country =
    components.find((c) => c.types.includes("country"))?.short_name || "";

  if (country !== "BR") return null;

  return city || state;
}

const getMainCity = (place) => {
  return getCityFromMap(place) || getCityFromAddress(place);
};

function toRad(v) {
  return (v * Math.PI) / 180;
}

function haversineKm(a, b) {
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);

  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return 2 * R * Math.asin(Math.sqrt(x));
}

function getComponentValue(place, type) {
  const comps = place?.address_components || [];
  const found = comps.find((c) => c.types?.includes(type));
  return found?.long_name || found?.short_name || "";
}

function getAirportCityText(place) {
  return normalizeText(
    getComponentValue(place, "locality") ||
      getComponentValue(place, "administrative_area_level_2") ||
      getComponentValue(place, "administrative_area_level_1") ||
      "",
  );
}

async function getQueryCenter(query) {
  const geocoder = new window.google.maps.Geocoder();
  const normalizedQuery = normalizeText(query);

  const { results } = await geocoder.geocode({
    address: `${query}, Brasil`,
    componentRestrictions: { country: "BR" },
  });

  if (!results?.length) return null;

  const scored = results.map((result) => {
    const formatted = normalizeText(result.formatted_address || "");
    const locality = normalizeText(
      result.address_components?.find((c) => c.types?.includes("locality"))
        ?.long_name || "",
    );
    const admin2 = normalizeText(
      result.address_components?.find((c) =>
        c.types?.includes("administrative_area_level_2"),
      )?.long_name || "",
    );

    let score = 0;

    if (locality === normalizedQuery) score += 100;
    if (admin2 === normalizedQuery) score += 90;
    if (formatted.includes(normalizedQuery)) score += 30;
    if (normalizedQuery.includes(locality) && locality) score += 20;

    return { result, score };
  });

  scored.sort((a, b) => b.score - a.score);

  const best = scored[0]?.result || results[0];
  const loc = best?.geometry?.location;

  if (!loc) return null;

  return { lat: loc.lat(), lng: loc.lng() };
}

function airportScore({ query, cityCenter, place, title, subtitle }) {
  const q = normalizeText(query);
  const titleN = normalizeText(title);
  const subtitleN = normalizeText(subtitle);
  const placeNameN = normalizeText(getPlaceName(place));
  const addressN = normalizeText(getFormattedAddress(place));
  const airportCityN = getAirportCityText(place);

  let score = 0;

  if (titleN.includes(q)) score += 120;
  if (placeNameN.includes(q)) score += 90;
  if (subtitleN.includes(q) || addressN.includes(q)) score += 20;

  if (titleN.includes("internacional")) score += 15;
  if (titleN.includes("aeroporto")) score += 10;

  if (airportCityN && airportCityN === q) score += 40;

  const pos = place?.geometry?.location;
  if (cityCenter && pos) {
    const airportPos = {
      lat: typeof pos.lat === "function" ? pos.lat() : pos.lat,
      lng: typeof pos.lng === "function" ? pos.lng() : pos.lng,
    };

    const distance = haversineKm(cityCenter, airportPos);

    if (distance > 200) score -= 120;
    else if (distance > 120) score -= 40;
    else score += Math.max(0, 60 - distance);
  }

  return score;
}

function OpcaoAviao() {
  const navigate = useNavigate();
  const location = useLocation();

  const [mapHeight, setMapHeight] = useState(350);

  const [showAirportModal, setShowAirportModal] = useState(false);

  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 935);
  const [step, setStep] = useState("origem");
  const [searchValue, setSearchValue] = useState("");
  const [searchByStep, setSearchByStep] = useState({
    origem: "",
    destino: "",
  });

  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shouldSearch, setShouldSearch] = useState(true);
  const [selectedOriginAirport, setSelectedOriginAirport] = useState(null);
  const [selectedDestinationAirport, setSelectedDestinationAirport] =
    useState(null);

  const [originPos, setOriginPos] = useState(null);
  const [destinationPos, setDestinationPos] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);

  const mapRef = useRef(null);
  const hasRestoredRef = useRef(false);
  const searchRequestIdRef = useRef(0);
  const autocompleteSessionTokenRef = useRef(null);
  const originMarkerRef = useRef(null);
  const destinationMarkerRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_API_KEY,
    language: "pt-BR",
    region: "BR",
  });

  useEffect(() => {
    const updateMapHeight = () => {
      if (window.innerWidth >= 935) return;

      // distância do topo da tela até o mapa
      const mapElement = document.querySelector(".map-side");

      if (!mapElement) return;

      const top = mapElement.getBoundingClientRect().top;

      // espaço para os botões e margem inferior
      const bottomSpace = 70;

      setMapHeight(window.innerHeight - top - bottomSpace);
    };

    updateMapHeight();

    window.addEventListener("resize", updateMapHeight);

    return () => window.removeEventListener("resize", updateMapHeight);
  }, [step]);

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 935);
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== "Enter") return;

      const tag = e.target.tagName;

      if (tag === "BUTTON") return;

      e.preventDefault();

      if (step === "origem") {
        handleNext();
        return;
      }

      if (step === "destino") {
        handleNext();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [step, selectedOriginAirport, selectedDestinationAirport]);

  useEffect(() => {
    saveToGlobalState({ cidades_trajeto: [] });
  }, []);

  useEffect(() => {
    if (mapRef.current) {
      window.google?.maps?.event?.trigger(mapRef.current, "resize");
    }
  }, [mapHeight]);

  useEffect(() => {
    const handleResize = () => {
      if (mapRef.current) {
        window.google?.maps?.event?.trigger(mapRef.current, "resize");

        if (originPos) {
          mapRef.current.setCenter(originPos);
        }
      }
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [originPos]);

  useEffect(() => {
    if (!isLoaded || !window.google || hasRestoredRef.current) return;

    const stored = getStoredData();

    const restoredOrigin = buildStoredAirport(stored, "origem");
    const restoredDestination = buildStoredAirport(stored, "destino");

    const restoredSearchByStep = {
      origem:
        restoredOrigin?.searchValue ||
        stored.search_origem ||
        restoredOrigin?.mainCity ||
        restoredOrigin?.name ||
        "",
      destino:
        restoredDestination?.searchValue ||
        stored.search_destino ||
        restoredDestination?.mainCity ||
        restoredDestination?.name ||
        "",
    };

    setSearchByStep(restoredSearchByStep);

    if (restoredOrigin) {
      setSelectedOriginAirport(restoredOrigin);
      setOriginPos(restoredOrigin.position);
    }

    if (restoredDestination) {
      setSelectedDestinationAirport(restoredDestination);
      setDestinationPos(restoredDestination.position);
    }

    const storedStep = stored.current_step;
    if (storedStep === "origem" || storedStep === "destino") {
      setStep(storedStep);
      setSearchValue(restoredSearchByStep[storedStep] || "");
      setMapCenter(
        storedStep === "origem"
          ? restoredOrigin?.position || defaultCenter
          : restoredDestination?.position ||
              restoredOrigin?.position ||
              defaultCenter,
      );
    } else if (restoredDestination) {
      setStep("destino");
      setSearchValue(restoredSearchByStep.destino);
      setMapCenter(restoredDestination.position);
    } else if (restoredOrigin) {
      setStep("origem");
      setSearchValue(restoredSearchByStep.origem);
      setMapCenter(restoredOrigin.position);
    }

    hasRestoredRef.current = true;
  }, [isLoaded]);

  useEffect(() => {
    const query = searchValue.trim();

    if (!query || query.length < 3 || !window.google) {
      if (query.length < 3) setAirports([]);
      return;
    }

    let cancelled = false;
    const requestId = ++searchRequestIdRef.current;

    const timeout = setTimeout(async () => {
      setLoading(true);

      try {
        const { AutocompleteSuggestion, AutocompleteSessionToken } =
          await window.google.maps.importLibrary("places");

        if (!autocompleteSessionTokenRef.current) {
          autocompleteSessionTokenRef.current = new AutocompleteSessionToken();
        }

        const cityCenter = await getQueryCenter(query).catch(() => null);

        const { suggestions } =
          await AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: query,
            includedPrimaryTypes: ["airport", "international_airport"],
            includedRegionCodes: ["BR"],
            language: "pt-BR",
            region: "BR",
            ...(cityCenter ? { origin: cityCenter } : {}),
            sessionToken: autocompleteSessionTokenRef.current,
          });

        if (cancelled || requestId !== searchRequestIdRef.current) return;

        const candidates = [];

        for (const s of suggestions || []) {
          const p = s.placePrediction;
          if (!p) continue;

          const place = await getPlaceDetailsFromPrediction(p);

          if (!isAirport(place)) continue;
          if (!isBrazilPlace(place)) continue;

          const status = String(place.businessStatus || "").toUpperCase();
          if (
            status.includes("CLOSED_PERMANENTLY") ||
            status.includes("CLOSED_TEMPORARILY")
          ) {
            continue;
          }

          const title = translateAirport(getTextValue(p.text));
          const subtitle = translateAirport(getTextValue(p.secondaryText));

          const score = airportScore({
            query,
            cityCenter,
            place,
            title,
            subtitle,
          });

          if (score < 40) continue;

          const rawTitle = getTextValue(p.text);
          const rawSubtitle = getTextValue(p.secondaryText);

          const hasIataCode =
            /\([A-Z0-9]{3}\)/.test(rawTitle) ||
            /\([A-Z0-9]{3}\)/.test(rawSubtitle);

          const isInternational =
            normalizeText(rawTitle).includes("internacional") ||
            normalizeText(rawSubtitle).includes("internacional");

          candidates.push({
            placePrediction: p,
            placeId: p.placeId,
            title,
            subtitle,
            score,
            hasIataCode,
            isInternational,
          });
        }

        candidates.sort((a, b) => b.score - a.score);

        const strongCandidates = candidates.filter(
          (item) => item.hasIataCode || item.isInternational,
        );

        const finalCandidates =
          strongCandidates.length > 0 ? strongCandidates : candidates;

        setAirports(finalCandidates.slice(0, 5));
      } catch (error) {
        if (!cancelled) setAirports([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [searchValue, shouldSearch, isLoaded]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !window.google) return;

    let cancelled = false;

    (async () => {
      const { AdvancedMarkerElement } =
        await window.google.maps.importLibrary("marker");

      if (cancelled) return;

      if (originMarkerRef.current) {
        originMarkerRef.current.map = null;
        originMarkerRef.current = null;
      }

      if (destinationMarkerRef.current) {
        destinationMarkerRef.current.map = null;
        destinationMarkerRef.current = null;
      }

      const map = mapRef.current;

      const showOriginMarker =
        step === "origem" ? !!originPos : !!originPos && !!destinationPos;

      if (showOriginMarker) {
        originMarkerRef.current = new AdvancedMarkerElement({
          map,
          position: originPos,
          title: "Origem",
          content: createMarkerContent("A"),
        });
      }

      if (destinationPos) {
        destinationMarkerRef.current = new AdvancedMarkerElement({
          map,
          position: destinationPos,
          title: "Destino",
          content: createMarkerContent("B"),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoaded, originPos, destinationPos, step]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !window.google) return;

    const raf = requestAnimationFrame(() => {
      const map = mapRef.current;
      if (!map) return;

      if (originPos && destinationPos) {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(originPos);
        bounds.extend(destinationPos);

        map.fitBounds(bounds, {
          top: 80,
          bottom: 80,
          left: 80,
          right: 80,
        });
        return;
      }

      if (originPos && destinationPos) {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(originPos);
        bounds.extend(destinationPos);

        map.fitBounds(bounds, {
          top: 80,
          bottom: 80,
          left: 80,
          right: 80,
        });
        return;
      }

      // quando já existem origem e destino
      if (originPos && destinationPos) {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(originPos);
        bounds.extend(destinationPos);

        map.fitBounds(bounds, {
          top: 80,
          bottom: 80,
          left: 80,
          right: 80,
        });

        return;
      }

      // origem selecionada
      if (step === "origem") {
        if (originPos) {
          map.setCenter(originPos);
          map.setZoom(airportZoom);
        } else {
          map.setCenter(defaultCenter);
          map.setZoom(defaultZoom);
        }

        return;
      }

      // destino
      if (step === "destino") {
        // enquanto não escolheu destino,
        // mostra o mapa do Brasil
        if (!destinationPos) {
          map.setCenter(defaultCenter);
          map.setZoom(defaultZoom);
          return;
        }

        map.setCenter(destinationPos);
        map.setZoom(airportZoom);
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [isLoaded, originPos, destinationPos, step]);

  const openAirportModal = () => {
    if (window.innerWidth < 935) {
      setShowAirportModal(true);
    }
  };

  async function getPlaceDetailsFromPrediction(placePrediction) {
    const place = placePrediction.toPlace();

    await place.fetchFields({
      fields: [
        "id",
        "displayName",
        "formattedAddress",
        "location",
        "types",
        "addressComponents",
        "businessStatus",
      ],
    });

    return {
      place_id: place.id,
      name: getPlaceName(place),
      formatted_address: getFormattedAddress(place),
      geometry: {
        location: getLocationLiteral(place.location),
      },
      types: place.types || [],
      businessStatus: place.businessStatus || "",
      address_components: normalizeAddressComponents(place),
    };
  }

  const persistStepSearch = (value) => {
    const key = step === "origem" ? "search_origem" : "search_destino";

    setSearchByStep((prev) => ({
      ...prev,
      [step]: value,
    }));

    saveToGlobalState({
      [key]: value,
    });
  };

  const handleSelectAirport = async (airport) => {
    try {
      const place = await getPlaceDetailsFromPrediction(
        airport.placePrediction,
      );

      if (!isAirport(place)) {
        alert("Selecione apenas aeroportos válidos.");
        return;
      }

      if (!isBrazilPlace(place)) {
        alert("Selecione apenas aeroportos do Brasil.");
        return;
      }

      const pos = place.geometry.location;

      const mainCity = await getMainCity(place);

      const airportPayload = {
        place_id: place.place_id || airport.placeId,
        name: place.name || airport.title,
        formatted_address: place.formatted_address || airport.subtitle || "",
        mainCity,
        position: pos,
      };

      const nextSearchValue = mainCity || airport.title || "";

      //console.log(airportPayload);

      if (step === "origem") {
        setSelectedOriginAirport(airportPayload);
        setOriginPos(pos);
        setMapCenter(pos);
        setSearchValue(nextSearchValue);
        setSearchByStep((prev) => ({ ...prev, origem: nextSearchValue }));

        saveToGlobalState({
          current_step: "origem",
          place_id_origem: airportPayload.place_id,
          cidade_origem: airportPayload.name,
          cidade_origem_main: mainCity,
          search_origem: nextSearchValue,
          lat_cidade_origem: pos.lat,
          lng_cidade_origem: pos.lng,
        });
      } else {
        setSelectedDestinationAirport(airportPayload);
        setDestinationPos(pos);
        setMapCenter(pos);
        setSearchValue(nextSearchValue);
        setSearchByStep((prev) => ({ ...prev, destino: nextSearchValue }));

        saveToGlobalState({
          current_step: "destino",
          place_id_destino: airportPayload.place_id,
          cidade_destino: airportPayload.name,
          cidade_destino_main: mainCity,
          search_destino: nextSearchValue,
          lat_cidade_destino: pos.lat,
          lng_cidade_destino: pos.lng,
        });
      }

      setShouldSearch(false);
    } catch (e) {
      console.error(e);
      alert("Não foi possível selecionar esse aeroporto.");
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setAirports([]);

    setShouldSearch(true);
    setSearchValue(value);
    persistStepSearch(value);

    if (step === "origem") {
      setSelectedOriginAirport(null);

      saveToGlobalState({
        cidade_origem: "",
        cidade_origem_main: "",
        search_origem: "",
        lat_cidade_origem: "",
        lng_cidade_origem: "",
      });
    } else {
      setSelectedDestinationAirport(null);

      saveToGlobalState({
        cidade_destino: "",
        cidade_destino_main: "",
        search_destino: "",
        lat_cidade_destino: "",
        lng_cidade_destino: "",
      });
    }
  };

  const handleBack = () => {
    if (step === "origem") {
      const data = getStoredData();

      const isSim = data?.voltando_trajeto === "sim";

      if (!isSim) {
        saveToGlobalState({
          voltando_trajeto: "nao",
        });
      }

      if (data?.editar_trajeto === "sim") {
        removeSpecificData("cidade_origem");
        removeSpecificData("cidade_origem_main");
        removeSpecificData("cidade_destino");
        removeSpecificData("cidade_destino_main");
        removeSpecificData("cidades_trajeto");
        removeSpecificData("current_step");
        removeSpecificData("lat_cidade_origem");
        removeSpecificData("lng_cidade_origem");
        removeSpecificData("lat_cidade_destino");
        removeSpecificData("lng_cidade_destino");
        removeSpecificData("horas_ida_viagem");
        removeSpecificData("horas_ida_destino");
        removeSpecificData("origemHora");
        removeSpecificData("destinoHora");
        removeSpecificData("origemData");
        removeSpecificData("destinoData");
        removeSpecificData("lat_cidade_origem");
        removeSpecificData("lng_cidade_origem");
        removeSpecificData("lat_cidade_destino");
        removeSpecificData("lng_cidade_destino");
        removeSpecificData("place_id_origem");
        removeSpecificData("place_id_destino");
        removeSpecificData("search_origem");
        removeSpecificData("search_destino");
        removeSpecificData("dia_mes_ano_ida_viagem");
        removeSpecificData("dia_mes_ano_ida_viagem_para_busca");
        removeSpecificData("dia_mes_ano_ida_destino");
        removeSpecificData("dia_mes_ano_ida_destino_para_busca");
        removeSpecificData("editar_trajeto");
        removeSpecificData("id");
        removeSpecificData("pais_origem");
        removeSpecificData("pais_destino");
        removeSpecificData("select_espaco_mala");
        removeSpecificData("qual_meio_transporte");
        removeSpecificData("voltando_trajeto");
        removeSpecificData("informacao_a_viagem");
        removeSpecificData("viagem_step");
        navigate("/dashboard-traveler");
        return;
      }

      removeSpecificData("qual_meio_transporte");

      navigate(isSim ? "/trips-progress-traveler" : "/qual-meio-transporte");
      return;
    }

    setStep("origem");
    setMapCenter(defaultCenter);

    if (mapRef.current) {
      mapRef.current.setCenter(defaultCenter);
      mapRef.current.setZoom(defaultZoom);
    }
    setSearchValue(
      searchByStep.origem ||
        selectedOriginAirport?.mainCity ||
        selectedOriginAirport?.name ||
        "",
    );
    setShouldSearch(true);
    setAirports([]);
    saveToGlobalState({ current_step: "origem" });
  };

  const handleNext = () => {
    if (step === "origem") {
      if (!selectedOriginAirport) {
        alert("Selecione um aeroporto primeiro.");
        return;
      }

      setStep("destino");

      setSearchValue(
        searchByStep.destino ||
          selectedDestinationAirport?.mainCity ||
          selectedDestinationAirport?.name ||
          "",
      );

      setMapCenter(defaultCenter);

      if (mapRef.current) {
        mapRef.current.setCenter(defaultCenter);
        mapRef.current.setZoom(defaultZoom);
      }
      setShouldSearch(true);
      setAirports([]);
      saveToGlobalState({ current_step: "destino" });
      return;
    }

    if (!selectedDestinationAirport) {
      alert("Selecione um aeroporto de destino primeiro.");
      return;
    }

    saveToGlobalState({ current_step: "destino" });
    navigate("/data");
  };

  const isSelected = (airport) => {
    const selected =
      step === "origem" ? selectedOriginAirport : selectedDestinationAirport;

    if (!selected) return false;

    if (selected.place_id && airport.placeId) {
      return selected.place_id === airport.placeId;
    }

    const a = selected.position;
    const b = airport.geometry?.location;

    if (a && b) {
      const lat = typeof b.lat === "function" ? b.lat() : b.lat;
      const lng = typeof b.lng === "function" ? b.lng() : b.lng;

      return a.lat === lat && a.lng === lng;
    }

    return false;
  };

  return (
    <>
      <div className="page-shell">
        <div className="main-container">
          <div className="content-side">
            <h1>
              {step === "origem"
                ? "QUAL CIDADE VOCÊ IRÁ SAIR?"
                : "QUAL O DESTINO?"}
            </h1>

            <input
              type="search"
              className="search-input"
              placeholder="Digite a cidade ou aeroporto..."
              value={searchValue}
              onChange={handleInputChange}
              onFocus={openAirportModal}
              onClick={openAirportModal}
            />

            {isLargeScreen && (
              <div className="results-card">
                <div className="airport-list">
                  {loading && (
                    <div className="spinner-container">
                      <div className="loading-spinner"></div>
                    </div>
                  )}

                  {airports.map((airport) => (
                    <button
                      key={airport.placeId}
                      className={`airport-item ${
                        isSelected(airport) ? "selected" : ""
                      }`}
                      onClick={() => handleSelectAirport(airport)}
                      type="button"
                    >
                      <strong>{airport.title}</strong>
                      <div>{airport.subtitle}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="actions-row">
              <button className="btn-back" onClick={handleBack} type="button">
                Voltar
              </button>

              <button className="btn-next" onClick={handleNext} type="button">
                {step === "origem" ? "Próximo" : "Avançar"}
              </button>
            </div>
          </div>

          <div
            className="map-side"
            style={
              !isLargeScreen
                ? {
                    height: `${mapHeight}px`,
                    minHeight: `${mapHeight}px`,
                  }
                : undefined
            }
          >
            {isLoaded && (
              <GoogleMap
                mapContainerStyle={{
                  width: "100%",
                  height: isLargeScreen ? "100%" : `${mapHeight}px`,
                }}
                center={mapCenter}
                zoom={defaultZoom}
                options={{ mapId }}
                onLoad={(map) => {
                  mapRef.current = map;

                  if (originPos && destinationPos) {
                    const bounds = new window.google.maps.LatLngBounds();
                    bounds.extend(originPos);
                    bounds.extend(destinationPos);
                    setTimeout(() => {
                      map.fitBounds(bounds);
                    }, 100);
                    return;
                  }

                  if (originPos) {
                    map.setCenter(originPos);
                    map.setZoom(14);
                    return;
                  }

                  if (destinationPos) {
                    map.setCenter(destinationPos);
                    map.setZoom(14);
                  }
                }}
              >
                {originPos && destinationPos && (
                  <PolylineF
                    path={[originPos, destinationPos]}
                    options={{
                      strokeColor: "#1e88e5",
                      strokeWeight: 4,
                      strokeOpacity: 0.8,
                    }}
                  />
                )}
              </GoogleMap>
            )}
          </div>
        </div>
      </div>
      {showAirportModal && (
        <div className="mobile-register-city-modal">
          <div
            style={{
              background: "linear-gradient(90deg,#1a7fe7,#19c4c9)",
              padding: 25,
              fontWeight: 800,
              color: "#fff",
            }}
          >
            {step === "origem" ? "CIDADE QUE IRÁ SAIR?" : "QUAL O DESTINO?"}
          </div>
          <button
            type="button"
            className="mobile-register-city-close"
            onClick={() => setShowAirportModal(false)}
          >
            ✕
          </button>
          <div className="mobile-register-city-header">
            <input
              autoFocus
              type="search"
              className="search-input"
              style={{ marginTop: 30, marginBottom: 10 }}
              placeholder="Digite a cidade ou aeroporto..."
              value={searchValue}
              onChange={handleInputChange}
            />
          </div>

          <div className="mobile-register-city-body">
            {loading && (
              <div className="spinner-container">
                <div className="loading-spinner"></div>
              </div>
            )}

            {airports.map((airport) => (
              <button
                key={airport.placeId}
                className={`airport-item ${
                  isSelected(airport) ? "selected" : ""
                }`}
                onClick={async () => {
                  await handleSelectAirport(airport);
                  setShowAirportModal(false);
                }}
                type="button"
                style={{
                  borderRadius: "18px",
                  display: "flex",
                  boxShadow: "0 5px 18px rgba(0,0,0,.08)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <LocationOn
                    sx={{
                      color: "#11b9a3",
                      mr: 2,
                    }}
                  />
                </div>
                <div>
                  <strong>{airport.title}</strong>
                  <div>{airport.subtitle}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      <StepFooter />
    </>
  );
}

export default OpcaoAviao;
